import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  DEFAULT_ESFERA_PRIVADA,
  PUBLICA_ESFERAS,
  PUBLICA_VALUE,
  NATUREZA_JURIDICA_OPTIONS,
  normalizeNaturezaFields,
} from '../../utils/naturezaJuridica';
import { required, phone, cpf, cnpj, cep, email } from '../../utils/validation';
import { formatPhone, formatCPF, formatCNPJ, formatCEP } from '../../utils/formatters';

const fieldValidators = {
  nomeRegistroMEC: [required],
  razaoSocial: [required],
  cnpj: [required, cnpj],
  naturezaJuridica: [required],
  endereco: [required],
  numero: [required],
  bairro: [required],
  cidadeId: [required],
  estado: [required],
  cep: [required, cep],
  telefone: [required, phone],
  telefone2: [phone],
  email: [required, email],
  email2: [email],
  reitorDiretor: [required],
  cpfReitorDiretor: [required, cpf],
  emailReitorDiretor: [required, email],
  telefoneReitorDiretor: [required, phone],
};

const validateField = (name, value) => {
  for (const validator of (fieldValidators[name] || [])) {
    const err = validator(value);
    if (err) return err;
  }
  return '';
};

const validateAll = (form) => {
  const errors = {};
  let hasErrors = false;
  for (const name of Object.keys(fieldValidators)) {
    const err = validateField(name, form[name]);
    if (err) {
      errors[name] = err;
      hasErrors = true;
    }
  }
  return { errors, hasErrors };
};

const formattedFields = {
  cnpj: formatCNPJ,
  cep: formatCEP,
  cpfReitorDiretor: formatCPF,
  telefone: formatPhone,
  telefone2: formatPhone,
  telefoneReitorDiretor: formatPhone,
};

const Container = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 30px auto 50px auto;
  position: relative;
  padding: 30px 24px;
  box-sizing: border-box;
  background-color: white;
  border-radius: 12px; /* bordas arredondadas */
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1); /* opcional, para dar destaque */
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px; /* Espaçamento vertical entre as linhas do formulário */
  width: 80%;
  margin: 0 auto; /* centraliza horizontalmente */
`;

const FormRow = styled.div`
  display: flex;
  gap: 20px; /* Espaçamento horizontal entre os campos na mesma linha */
  flex-wrap: wrap; /* Garante que os itens quebrem para a próxima linha em telas menores */
`;

const FormItem = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1; /* Permite que o item cresça e ocupe o espaço disponível */
  min-width: 150px; /* Garante uma largura mínima para que os campos não fiquem muito apertados */
`;

const Label = styled.label`
  display: block;
  margin-bottom: 2px;
  font-weight: bold;
  font-size: 0.9rem;
  color: #333;
`;

const Input = styled.input`
  width: 100%; /* Ocupa 100% da largura do seu container pai (FormItem) */
  height: 35px; /* Altura fixa para todos os inputs, padronizando o visual */
  padding: 8px 12px; /* Padding interno para o texto */
  border: 1px solid ${props => props.$hasError ? '#c0392b' : '#ccc'};
  border-radius: 8px;
  font-size: 1rem;
  box-sizing: border-box; /* Inclui padding e borda na largura total */

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#c0392b' : '#6ab8a8'}; /* Destaca o input ao ser focado */
    box-shadow: 0 0 0 2px ${props => props.$hasError ? 'rgba(192, 57, 43, 0.3)' : 'rgba(106, 184, 168, 0.3)'};
  }
`;

const Title = styled.h2`
  text-align: center;
  font-weight: 800;
  font-size: 1.2rem;
  color: #f28c28;
  margin-bottom: 24px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
  justify-content: center; /* centraliza horizontalmente */
`;

const Button = styled.button`
  background-color: #6ab292;
  color: white;
  font-size: 15px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  padding: 8px 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.2);

  &:hover {
    background-color: #5a9e82;
  }
`;

const Select = styled.select`
  width: 100%;
  height: 35px;
  padding: 8px 12px;
  border: 1px solid ${props => props.$hasError ? '#c0392b' : '#ccc'};
  border-radius: 8px;
  font-size: 1rem;
  box-sizing: border-box;
  background-color: white;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#c0392b' : '#6ab8a8'};
    box-shadow: 0 0 0 2px ${props => props.$hasError ? 'rgba(192, 57, 43, 0.3)' : 'rgba(106, 184, 168, 0.3)'};
  }
`;

const CitySearchContainer = styled.div`
  position: relative;
`;

const CityDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ccc;
  border-top: none;
  border-radius: 0 0 8px 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const CityOption = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const ErrorMessage = styled.div`
  color: #c0392b;
  font-size: 0.8rem;
  margin-top: 4px;
`;

const DEFAULT_FORM = {
  nomeRegistroMEC: "",
  sigla: "",
  razaoSocial: "",
  cnpj: "",
  naturezaJuridica: "",
  esfera: DEFAULT_ESFERA_PRIVADA,
  endereco: "",
  numero: "",
  bairro: "",
  cidadeId: "",
  estado: "",
  cep: "",
  telefone: "",
  telefone2: "",
  email: "",
  email2: "",
  reitorDiretor: "",
  cpfReitorDiretor: "",
  emailReitorDiretor: "",
  telefoneReitorDiretor: "",
  numeroCooperacao: "",
  vigenciaTermo: "",
  documento: null,
};

const getInitialForm = () => {
  const saved = localStorage.getItem('cadastroInstituicaoForm');
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      return normalizeNaturezaFields({ ...DEFAULT_FORM, ...parsed });
    } catch (_) {}
  }
  return { ...DEFAULT_FORM };
};

const FormComponent = () => {
  const [form, setForm] = useState(getInitialForm);
  const [estados, setEstados] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [citySearch, setCitySearch] = useState(() => form.cidade || '');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [filteredCities, setFilteredCities] = useState([]);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(true);
  const [hasValidTermo, setHasValidTermo] = useState(false);
  const navigate = useNavigate();

  const handleBefore = () => {
    navigate('/PreenchaDados');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const formatter = formattedFields[name];
    const formattedValue = formatter ? formatter(value) : value;
    setForm(prev => ({ ...prev, [name]: formattedValue }));
    if (touched[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: validateField(name, formattedValue) }));
    }
    if (error) setError('');
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setFieldErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const getError = (name) => (touched[name] ? fieldErrors[name] : '');

  const handleNaturezaChange = (e) => {
    const selectedNatureza = e.target.value;
    setForm(prev => {
      const isPublica = selectedNatureza === PUBLICA_VALUE;
      const nextEsfera = isPublica
        ? (PUBLICA_ESFERAS.includes(prev.esfera) ? prev.esfera : PUBLICA_ESFERAS[0])
        : DEFAULT_ESFERA_PRIVADA;

      return { ...prev, naturezaJuridica: selectedNatureza, esfera: nextEsfera };
    });
    if (touched.naturezaJuridica) {
      setFieldErrors(prev => ({ ...prev, naturezaJuridica: validateField('naturezaJuridica', selectedNatureza) }));
    }
    if (error) setError('');
  };

  const handleEsferaChange = (e) => {
    const { value } = e.target;
    if (!PUBLICA_ESFERAS.includes(value)) {
      return;
    }
    setForm(prev => ({ ...prev, esfera: value }));
  };

  const handleEstadoChange = async (e) => {
    const estadoId = e.target.value;
    setForm(prev => ({ ...prev, estado: estadoId, cidadeId: '', cidade: '' }));
    setCitySearch('');
    if (touched.estado) {
      setFieldErrors(prev => ({ ...prev, estado: validateField('estado', estadoId) }));
    }
    if (error) setError('');

    if (estadoId) {
      try {
        const cidadesData = await api.getCidadesByEstado(estadoId);
        setCidades(cidadesData);
        setFilteredCities(cidadesData);
      } catch (error) {
        console.error('Erro ao carregar cidades:', error);
        setError('Erro ao carregar cidades');
      }
    } else {
      setCidades([]);
      setFilteredCities([]);
    }
  };

  const handleCitySearch = (e) => {
    const value = e.target.value;
    setCitySearch(value);
    setShowCityDropdown(true);

    if (form.cidadeId) {
      setForm(prev => ({ ...prev, cidadeId: '', cidade: '' }));
      if (touched.cidadeId) {
        setFieldErrors(prev => ({ ...prev, cidadeId: validateField('cidadeId', '') }));
      }
    }
    if (error) setError('');

    if (value.trim()) {
      const filtered = cidades.filter(cidade =>
        cidade.nome.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCities(filtered);
    } else {
      setFilteredCities(cidades);
    }
  };

  const handleCitySelect = (cidade) => {
    setForm(prev => ({ ...prev, cidadeId: cidade.id, cidade: cidade.nome }));
    setCitySearch(cidade.nome);
    setShowCityDropdown(false);
    setFieldErrors(prev => ({ ...prev, cidadeId: '' }));
    if (error) setError('');
  };

  // Check if user has valid termo and load states on component mount
  useEffect(() => {
    const initializeComponent = async () => {
      try {
        // Check if user has uploaded termo
        const termo = await api.getMeuTermo();
        setHasValidTermo(true);

        // Load states
        const estadosData = await api.getEstados();
        setEstados(estadosData);

        // Se o form ja tem um estado salvo (ex.: voltando da etapa de confirmacao),
        // carrega as cidades correspondentes para manter o dropdown funcional
        if (form.estado) {
          const cidadesData = await api.getCidadesByEstado(form.estado);
          setCidades(cidadesData);
          setFilteredCities(cidadesData);
        }

      } catch (error) {
        console.error('Erro ao inicializar componente:', error);
        if (error.message.includes('termo')) {
          setError('Você precisa fazer o upload do termo de cooperação antes de preencher os dados da instituição.');
          setHasValidTermo(false);
        } else {
          setError('Erro ao carregar dados necessários');
        }
      } finally {
        setLoading(false);
      }
    };
    initializeComponent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load cities when estado changes
  useEffect(() => {
    if (form.estado && form.cidadeId) {
      // Set city search to the selected city name
      const selectedCity = cidades.find(c => c.id === parseInt(form.cidadeId));
      if (selectedCity) {
        setCitySearch(selectedCity.nome);
      }
    }
  }, [form.estado, form.cidadeId, cidades]);

  useEffect(() => {
    localStorage.setItem('cadastroInstituicaoForm', JSON.stringify(form));
  }, [form]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    const allTouched = Object.keys(fieldValidators).reduce((acc, key) => ({ ...acc, [key]: true }), {});
    setTouched(allTouched);

    const { errors, hasErrors } = validateAll(form);
    setFieldErrors(errors);

    if (hasErrors) {
      setError('Por favor, corrija os erros antes de prosseguir.');
      return;
    }

    localStorage.setItem('cadastroInstituicaoForm', JSON.stringify(form));
    navigate('/CadastroFeito', { state: form });
  };

  if (loading) {
    return (
      <Container>
        <Title>CARREGANDO...</Title>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ display: 'inline-block', width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #6ab8a8', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        </div>
      </Container>
    );
  }

  if (!hasValidTermo) {
    return (
      <Container>
        <Title>TERMO DE COOPERAÇÃO NECESSÁRIO</Title>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <ButtonContainer>
            <Button onClick={() => navigate('/PreenchaDados')}>
              Fazer Upload do Termo
              <span className="material-symbols-outlined" style={{ fontSize: '15px'}}>arrow_forward_ios</span>
            </Button>
          </ButtonContainer>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <form onSubmit={handleSubmit} noValidate>
        <Title>PREENCHA OS DADOS DA INSTITUIÇÃO</Title>
        <FormWrapper>
          <FormRow>
            <FormItem>
              <Label htmlFor="nomeRegistroMEC">Nome de registro no MEC*</Label>
              <Input
                type="text"
                id="nomeRegistroMEC"
                name="nomeRegistroMEC"
                value={form.nomeRegistroMEC}
                onChange={handleChange}
                onBlur={handleBlur}
                $hasError={!!getError('nomeRegistroMEC')}
              />
              {getError('nomeRegistroMEC') && <ErrorMessage>{getError('nomeRegistroMEC')}</ErrorMessage>}
            </FormItem>
            <FormItem>
              <Label htmlFor="sigla">Sigla</Label>
              <Input
                type="text"
                id="sigla"
                name="sigla"
                value={form.sigla}
                onChange={handleChange}
              />
            </FormItem>
          </FormRow>

          <FormRow>
            <FormItem>
              <Label htmlFor="razaoSocial">Razão Social*</Label>
              <Input
                type="text"
                id="razaoSocial"
                name="razaoSocial"
                value={form.razaoSocial}
                onChange={handleChange}
                onBlur={handleBlur}
                $hasError={!!getError('razaoSocial')}
              />
              {getError('razaoSocial') && <ErrorMessage>{getError('razaoSocial')}</ErrorMessage>}
            </FormItem>
            <FormItem>
              <Label htmlFor="cnpj">CNPJ*</Label>
              <Input
                type="text"
                id="cnpj"
                name="cnpj"
                value={form.cnpj}
                onChange={handleChange}
                onBlur={handleBlur}
                $hasError={!!getError('cnpj')}
                placeholder="00.000.000/0000-00"
              />
              {getError('cnpj') && <ErrorMessage>{getError('cnpj')}</ErrorMessage>}
            </FormItem>
          </FormRow>

          <FormRow>
            <FormItem>
              <Label htmlFor="naturezaJuridica">Natureza Jurídica*</Label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  marginTop: "2px",
                }}
              >
                <Select
                  id="naturezaJuridica"
                  name="naturezaJuridica"
                  value={form.naturezaJuridica}
                  onChange={handleNaturezaChange}
                  onBlur={handleBlur}
                  $hasError={!!getError('naturezaJuridica')}
                  style={{ flex: "0 0 300px" }}
                >
                  <option value="">Selecione a natureza jurídica</option>
                  {NATUREZA_JURIDICA_OPTIONS.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>

                {form.naturezaJuridica === PUBLICA_VALUE ? (
                  <div
                    style={{
                      display: "flex",
                      gap: "15px",
                    }}
                  >
                    {PUBLICA_ESFERAS.map(option => (
                      <label key={option} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <input
                          type="radio"
                          name="esfera"
                          value={option}
                          checked={form.esfera === option}
                          onChange={handleEsferaChange}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                ) : (
                  form.naturezaJuridica && (
                    <div style={{padding: "6px 12px", fontWeight: 400}}>
                      Esfera: {DEFAULT_ESFERA_PRIVADA}
                    </div>
                  )
                )}
              </div>
              {getError('naturezaJuridica') && <ErrorMessage>{getError('naturezaJuridica')}</ErrorMessage>}
            </FormItem>
          </FormRow>

          <FormRow>
            <FormItem>
              <Label htmlFor="endereco">Endereço*</Label>
              <Input
                type="text"
                id="endereco"
                name="endereco"
                value={form.endereco}
                onChange={handleChange}
                onBlur={handleBlur}
                $hasError={!!getError('endereco')}
              />
              {getError('endereco') && <ErrorMessage>{getError('endereco')}</ErrorMessage>}
            </FormItem>
            <FormItem style={{ flex: "0.4", minWidth: "120px" }}>
              <Label htmlFor="numero">Número*</Label>
              <Input
                type="text"
                id="numero"
                name="numero"
                value={form.numero}
                onChange={handleChange}
                onBlur={handleBlur}
                $hasError={!!getError('numero')}
              />
              {getError('numero') && <ErrorMessage>{getError('numero')}</ErrorMessage>}
            </FormItem>
          </FormRow>

          <FormRow>
            <FormItem>
              <Label htmlFor="bairro">Bairro*</Label>
              <Input
                type="text"
                id="bairro"
                name="bairro"
                value={form.bairro}
                onChange={handleChange}
                onBlur={handleBlur}
                $hasError={!!getError('bairro')}
              />
              {getError('bairro') && <ErrorMessage>{getError('bairro')}</ErrorMessage>}
            </FormItem>
            <FormItem>
              <Label htmlFor="estado">Estado*</Label>
              <Select
                id="estado"
                name="estado"
                value={form.estado}
                onChange={handleEstadoChange}
                onBlur={handleBlur}
                $hasError={!!getError('estado')}
              >
                <option value="">Selecione o estado</option>
                {estados.map(estado => (
                  <option key={estado.id} value={estado.id}>
                    {estado.nome} ({estado.sigla})
                  </option>
                ))}
              </Select>
              {getError('estado') && <ErrorMessage>{getError('estado')}</ErrorMessage>}
            </FormItem>
            <FormItem>
              <Label htmlFor="cidadeId">Cidade*</Label>
              <CitySearchContainer>
                <Input
                  type="text"
                  id="cidadeId"
                  name="cidadeId"
                  value={citySearch}
                  onChange={handleCitySearch}
                  onFocus={() => setShowCityDropdown(true)}
                  onBlur={() => {
                    setTimeout(() => setShowCityDropdown(false), 200);
                    setTouched(prev => ({ ...prev, cidadeId: true }));
                    setFieldErrors(prev => ({ ...prev, cidadeId: validateField('cidadeId', form.cidadeId) }));
                  }}
                  placeholder={form.estado ? 'Digite para buscar uma cidade' : 'Selecione um estado primeiro'}
                  disabled={!form.estado}
                  $hasError={!!getError('cidadeId')}
                />
                {showCityDropdown && filteredCities.length > 0 && (
                  <CityDropdown>
                    {filteredCities.slice(0, 10).map(cidade => (
                      <CityOption
                        key={cidade.id}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          handleCitySelect(cidade);
                        }}
                      >
                        {cidade.nome}
                      </CityOption>
                    ))}
                  </CityDropdown>
                )}
              </CitySearchContainer>
              {getError('cidadeId') && <ErrorMessage>{getError('cidadeId')}</ErrorMessage>}
            </FormItem>
          </FormRow>

          <FormRow>
            <FormItem>
              <Label htmlFor="cep">CEP*</Label>
              <Input
                type="text"
                id="cep"
                name="cep"
                value={form.cep}
                onChange={handleChange}
                onBlur={handleBlur}
                $hasError={!!getError('cep')}
              />
              {getError('cep') && <ErrorMessage>{getError('cep')}</ErrorMessage>}
            </FormItem>
            <FormItem>
              <Label htmlFor="telefone">Telefone*</Label>
              <Input
                type="tel"
                id="telefone"
                name="telefone"
                value={form.telefone}
                onChange={handleChange}
                onBlur={handleBlur}
                $hasError={!!getError('telefone')}
              />
              {getError('telefone') && <ErrorMessage>{getError('telefone')}</ErrorMessage>}
            </FormItem>
            <FormItem>
              <Label htmlFor="telefone2">Telefone (opcional)</Label>
              <Input
                type="tel"
                id="telefone2"
                name="telefone2"
                value={form.telefone2}
                onChange={handleChange}
                onBlur={handleBlur}
                $hasError={!!getError('telefone2')}
              />
              {getError('telefone2') && <ErrorMessage>{getError('telefone2')}</ErrorMessage>}
            </FormItem>
          </FormRow>

          <FormRow>
            <FormItem>
              <Label htmlFor="email">Email*</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                onBlur={handleBlur}
                $hasError={!!getError('email')}
              />
              {getError('email') && <ErrorMessage>{getError('email')}</ErrorMessage>}
            </FormItem>
            <FormItem>
              <Label htmlFor="email2">Email (opcional)</Label>
              <Input
                type="email"
                id="email2"
                name="email2"
                value={form.email2}
                onChange={handleChange}
                onBlur={handleBlur}
                $hasError={!!getError('email2')}
              />
              {getError('email2') && <ErrorMessage>{getError('email2')}</ErrorMessage>}
            </FormItem>
          </FormRow>

          <FormRow>
            <FormItem>
              <Label htmlFor="reitorDiretor">Reitor/Diretor*</Label>
              <Input
                type="text"
                id="reitorDiretor"
                name="reitorDiretor"
                value={form.reitorDiretor}
                onChange={handleChange}
                onBlur={handleBlur}
                $hasError={!!getError('reitorDiretor')}
              />
              {getError('reitorDiretor') && <ErrorMessage>{getError('reitorDiretor')}</ErrorMessage>}
            </FormItem>
            <FormItem>
              <Label htmlFor="cpfReitorDiretor">CPF*</Label>
              <Input
                type="text"
                id="cpfReitorDiretor"
                name="cpfReitorDiretor"
                value={form.cpfReitorDiretor}
                onChange={handleChange}
                onBlur={handleBlur}
                $hasError={!!getError('cpfReitorDiretor')}
              />
              {getError('cpfReitorDiretor') && <ErrorMessage>{getError('cpfReitorDiretor')}</ErrorMessage>}
            </FormItem>
          </FormRow>

          <FormRow>
            <FormItem>
              <Label htmlFor="emailReitorDiretor">Email (Reitor/Diretor)*</Label>
              <Input
                type="email"
                id="emailReitorDiretor"
                name="emailReitorDiretor"
                value={form.emailReitorDiretor}
                onChange={handleChange}
                onBlur={handleBlur}
                $hasError={!!getError('emailReitorDiretor')}
              />
              {getError('emailReitorDiretor') && <ErrorMessage>{getError('emailReitorDiretor')}</ErrorMessage>}
            </FormItem>
            <FormItem>
              <Label htmlFor="telefoneReitorDiretor">Telefone*</Label>
              <Input
                type="tel"
                id="telefoneReitorDiretor"
                name="telefoneReitorDiretor"
                value={form.telefoneReitorDiretor}
                onChange={handleChange}
                onBlur={handleBlur}
                $hasError={!!getError('telefoneReitorDiretor')}
              />
              {getError('telefoneReitorDiretor') && <ErrorMessage>{getError('telefoneReitorDiretor')}</ErrorMessage>}
            </FormItem>
          </FormRow>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <ButtonContainer>
            <Button type="button" onClick={handleBefore}>
            <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>arrow_back_ios</span>
            Voltar
          </Button>
          <Button type="submit">
            Próximo  
            <span className="material-symbols-outlined" style={{ fontSize: '15px'}}>arrow_forward_ios</span>
          </Button>
        </ButtonContainer>
        </FormWrapper>
      </form>
    </Container>
  );
};

export default FormComponent;
